import { useRouter } from 'next/router';
import styled from 'styled-components';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import Banner from '@/components/banner';
import CategoriesGrid from '@/components/categories-grid';
import HowItWorks from '@/components/how-it-works';
import SEO from '@/components/seo';
import enabledCurrencies from '@data/enabled-currencies';
import { shareImageForCurrency } from '@/utils';
import config from '@/config';
import { media } from '@/themes';
import translatedPaths from '@/paths.json';
import i18n from '../../i18n.json';

const Container = styled.div`
  margin: 0 auto;
  max-width: ${props => props.theme.maxContentWidth};
`;

const LogoImage = styled.img`
  width: 5em;
  height: 5em;
  margin-right: 1.5em;
  display: inline-block;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.primary};
  margin-top: 0;
  margin-bottom: .25em;
`;

const Subtitle = styled.h3`
  color: ${({ theme }) => theme.colors.typography.secondary};
  margin: 0;
`;

const BannerContent = styled.div`
  align-items: center;
  display: flex;
  flex-grow: 1;
  text-align: left;

  ${media.md`
    display: block;
    text-align: center;
  `};
`;

const LeftColumn = styled.div`
  flex-basis: 0;
  flex-grow: 1;

  ${media.sm`
    margin-bottom: 1em;
  `};
`;

const RightColumn = styled.div`
  align-items: center;
  display: flex;
  flex-basis: 0;
  flex-grow: 1;
  text-align: left;
`;

const Illustration = styled(Image)``;

const Index = ({ currency, categories }) => {
  const router = useRouter();
  const t = useTranslations('currency');
  const seoTitle = t('seoTitle', { currency: currency.displayName, symbol: currency.displaySymbol });
  const seoDescription = t('seoDescription', { currency: currency.displayName, symbol: currency.displaySymbol });
  const url = `${config.rootUrl}${router.asPath}`;

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        url={url}
        imageUrl={shareImageForCurrency(currency)}
      />
      <Banner style={{ padding: 0 }} light>
        <BannerContent>
          <LeftColumn>
            <Illustration
              alt="shopping illustration"
              height={374}
              src="/images/shopping.svg"
              unoptimized={true}
              width={576} />
          </LeftColumn>
          <RightColumn>
            <LogoImage src={currency.image} unoptimized alt={`${currency.displayName} - ${currency.displaySymbol}`} />
            <div>
              <Title>{t('title', { currency: currency.displayName })}</Title>
              <Subtitle>{t('subTitle', { currency: currency.displayName })}</Subtitle>
            </div>
          </RightColumn>
        </BannerContent>
      </Banner>
      <Container>
        <CategoriesGrid categories={categories} currency={currency} />
      </Container>
      <HowItWorks />
    </>
  );
};

export async function getStaticPaths ({ locales }) {
  const paths = enabledCurrencies.map(currency => {
    return {
      params: {
        slug: currency.fiat
          ? `${translatedPaths.currencies.fiat[i18n.defaultLocale]}-${currency.slug}`
          : `${translatedPaths.currencies.crypto[i18n.defaultLocale]}-${currency.slug}`
      }
    };
  });

  for (let i = 0; i < locales.length; i++) {
    const locale = locales[i];

    enabledCurrencies.forEach(currency => {
      locale !== i18n.defaultLocale && paths.push({
        params: {
          slug: currency.fiat
            ? `${translatedPaths.currencies.fiat[locale]}-${currency.slug}`
            : `${translatedPaths.currencies.crypto[locale]}-${currency.slug}`
        },
        locale
      })
    });
  }

  return {
    paths,
    fallback: false
  };
}

export async function getStaticProps ({ params, locale }) {
  //TODO: Error handling and log to Bugsnag
  const paths = Object.keys(translatedPaths.currencies)
    .reduce((acc, curr) => {
      acc = acc
        .concat(Object.keys(translatedPaths.currencies[curr])
        .map(locale => translatedPaths.currencies[curr][locale]));

      return acc;
    }, []);


  const currency = enabledCurrencies.find(c => {
    let { slug: currencySlug } = params;

    paths.forEach(path => {
      currencySlug = currencySlug.replace(`${path}-`, '')
    });

    return c.slug === currencySlug;
  });

  return {
    props: {
      currency,
      hideCurrencies: true,
      messages: require(`@/locales/${locale}.json`)
    }
  };
}

export default Index;
